<template>
    <div class="finance-page banking deposit-page">
        <div class="finance-page__inner">
            <header class="finance-hero">
                <div class="finance-hero__icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                </div>
                <div>
                    <h1 class="finance-hero__title">{{ $t('deposit.deposit_form') }}</h1>
                    <p class="finance-hero__subtitle">
                        {{ $store.state.language === 'en'
                            ? 'Transfer to the account below, then submit your amount and reference.'
                            : 'အောက်ပါအကောင့်သို့ လွှဲပြီးနောက် ပမာဏနှင့် မှတ်တမ်းကို ထည့်သွင်းပါ။' }}
                    </p>
                </div>
            </header>

            <div v-if="error" class="finance-alert finance-alert--error" role="alert">
                {{ error }}
            </div>

            <form class="finance-form" @submit.prevent>
                <section class="finance-card finance-card--account-select">
                    <div class="finance-account-section__head">
                        <h2 class="finance-card__label finance-card__label--inline">{{ $t('deposit.choose_payment_type') }}</h2>
                        <span v-if="accounts.length" class="finance-account-section__count">{{ accounts.length }}</span>
                    </div>
                    <p class="finance-select-hint">
                        {{
                            $store.state.language === 'en'
                                ? 'Choose the wallet or bank you will transfer from.'
                                : 'ငွေလွှဲမည့် ပိုက်ဆံအိတ် သို့မဟုတ် ဘဏ်ကို ရွေးချယ်ပါ။'
                        }}
                    </p>

                    <div v-if="!accounts.length" class="finance-method-empty">
                        {{
                            $store.state.language === 'en'
                                ? 'No payment methods available yet.'
                                : 'ငွေပေးချေမှု နည်းလမ်းများ မရှိသေးပါ။'
                        }}
                    </div>

                    <div
                        v-else
                        class="finance-method-list"
                        role="listbox"
                        :aria-label="$store.state.language === 'en' ? 'Payment methods' : 'ငွေပေးချေမှု နည်းလမ်း'"
                    >
                        <input
                            v-if="accounts.length > 6"
                            v-model="accountFilter"
                            type="search"
                            class="finance-method-filter"
                            :placeholder="$store.state.language === 'en' ? 'Search…' : 'ရှာဖွေရန်…'"
                            autocomplete="off"
                        />
                        <button
                            v-for="acc in filteredAccounts"
                            :key="acc.id"
                            type="button"
                            role="option"
                            :aria-selected="selected_account.account_id == acc.id"
                            class="finance-method-row"
                            :class="{ 'is-selected': selected_account.account_id == acc.id }"
                            @click="selectPaymentAccount(acc)"
                        >
                            <span class="finance-method-row__logo" aria-hidden="true">
                                <img
                                    v-if="paymentTypeImage(acc)"
                                    :src="paymentTypeImage(acc)"
                                    :alt="paymentTypeLabel(acc)"
                                >
                                <span
                                    v-else
                                    class="finance-method-row__logo-fallback"
                                >{{ paymentTypeInitial(acc) }}</span>
                            </span>
                            <span class="finance-method-row__body">
                                <span class="finance-method-row__title text-capitalize">{{ paymentTypeLabel(acc) }}</span>
                                <span v-if="paymentTypeSubtitle(acc)" class="finance-method-row__sub">{{ paymentTypeSubtitle(acc) }}</span>
                            </span>
                            <span class="finance-method-row__radio" aria-hidden="true">
                                <span class="finance-method-row__radio-dot" />
                            </span>
                        </button>
                        <p v-if="accounts.length > 6 && !filteredAccounts.length" class="finance-method-filter-empty">
                            {{ $store.state.language === 'en' ? 'No matches.' : 'မတွေ့ပါ။' }}
                        </p>
                    </div>
                    <p v-if="errors.account" class="finance-field-error">{{ errors.account }}</p>
                </section>

                <section v-if="account.length !== 0" class="finance-card finance-card--accent">
                    <div class="finance-bank-head">
                        <span class="finance-bank-head__logo" v-if="paymentTypeImage(account[0])">
                            <img :src="paymentTypeImage(account[0])" :alt="paymentTypeLabel(account[0])">
                        </span>
                        <span class="finance-bank-head__name">{{ paymentTypeLabel(account[0]) }}</span>
                    </div>
                    <div class="finance-account-box">
                        <span class="finance-account-box__label">{{ account[0].account_name }}</span>
                        <p class="finance-account-box__number" id="textToCopy">{{ account[0].number }}</p>
                        <button type="button" class="finance-btn-copy" @click="copyText">
                            {{ $store.state.language === 'en' ? 'Copy number' : 'နံပါတ်ကူးရန်' }}
                        </button>
                    </div>
                    <p v-if="account[0].note" class="finance-note">{{ account[0].note }}</p>
                </section>

                <section class="finance-card">
                    <label class="finance-label" for="deposit-amount">{{ $t('deposit.amount') }}</label>
                    <p class="finance-hint">{{ $t('deposit.minimum_deposit') }}</p>
                    <input
                        id="deposit-amount"
                        type="number"
                        inputmode="decimal"
                        class="finance-input"
                        v-model="depositData.amount"
                        :placeholder="$store.state.language === 'en' ? 'e.g. 50000' : 'ငွေပမာဏ'"
                    />
                    <p v-if="errors.amount" class="finance-field-error">{{ errors.amount }}</p>
                </section>

                <section class="finance-card">
                    <label class="finance-label">{{ $t('deposit.screenshot') }}</label>
                    <p class="finance-hint">{{ $t('deposit.account_remark') }}</p>
                    <div class="finance-uploader-wrap">
                        <van-uploader
                            v-model="imageUploader"
                            :max-count="1"
                            accept="image/*"
                            preview-size="5.5rem"
                        />
                    </div>
                    <p v-if="errors.image" class="finance-field-error">{{ errors.image }}</p>
                </section>

                <section class="finance-card">
                    <label class="finance-label" for="deposit-remark">{{ $t('deposit.remark') }}</label>
                    <p class="finance-hint">
                        {{
                            $store.state.language === 'en'
                                ? 'Optional note about your transfer.'
                                : 'ငွေလွှဲမှတ်ချက် (မထည့်လည်းရပါသည်)။'
                        }}
                    </p>
                    <textarea
                        id="deposit-remark"
                        rows="3"
                        class="finance-input finance-textarea"
                        v-model="depositData.remark"
                        :placeholder="$store.state.language === 'en' ? 'Remark' : 'မှတ်ချက်'"
                    />
                    <p v-if="errors.remark" class="finance-field-error">{{ errors.remark }}</p>
                </section>

                <button
                    type="button"
                    class="finance-btn-primary"
                    :disabled="isConfirm"
                    @click="sendDepositData()"
                >
                    <Loading v-if="isConfirm" color="#0d0d0d" size="24" class="finance-btn-primary__spin"/>
                    <span v-else>{{ $t('deposit.submit') }}</span>
                </button>
            </form>
        </div>
    </div>
</template>
<script>
import { Toast, Loading } from 'vant';
import { mapActions } from 'vuex';
export default {
  components: { Loading },
  data() {
    return {
      accountFilter: '',
      selected_account: {
        account_id: null,
      },
      accounts: [],
      account: [],
      imageUploader: [],
      depositData: {},
      errors: {},
      isConfirm: false,
      error: null,
    };
  },
  methods: {
    ...mapActions(['fetchUser']),
    selectPaymentAccount(acc) {
      if (!acc || acc.id == null) return;
      this.selected_account.account_id = acc.id;
    },
    paymentTypeImage(acc) {
      if (!acc) return null;
      return acc.link || acc.payment_type?.image || null;
    },
    paymentTypeLabel(acc) {
      if (!acc) return '';
      return acc.payment_type?.name || acc.name || acc.account_name || '';
    },
    paymentTypeSubtitle(acc) {
      if (!acc) return '';
      const label = this.paymentTypeLabel(acc);
      if (acc.account_name && acc.account_name !== label) {
        return acc.account_name;
      }
      if (acc.number) {
        return acc.number;
      }
      return '';
    },
    paymentTypeInitial(acc) {
      const label = this.paymentTypeLabel(acc);
      return label ? label.charAt(0).toUpperCase() : '?';
    },
    normalizeAccount(acc) {
      return {
        ...acc,
        link: acc.payment_type?.image || acc.link || null,
      };
    },
    copyText() {
      const el = document.getElementById('textToCopy');
      if (!el) return;
      const textToCopy = el.innerText;
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      Toast.success(
        this.$store.state.language === 'en' ? 'Copied to clipboard' : 'ကူးယူပြီးပါပြီ',
      );
    },
    async fetchAccounts() {
      try {
        const res = await axios.get('/accounts', {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  },
              });
        const rows = res.data.data || [];
        this.accounts = rows
          .filter((acc) => !acc.is_disable)
          .map((acc) => this.normalizeAccount(acc));
      } catch (error) {
        console.log(error);
      }
    },
    async sendDepositData() {
      try {
        await this.fetchUser();
        this.errors = {};
        if (!this.selected_account.account_id) {
          this.errors.account = this.$store.state.language === 'en'
            ? 'Please pick account type'
            : 'အကောင့်အမျိုးအစား ရွေးချယ်ပေးပါ။';
          return false;
        }
        if (!this.depositData.amount) {
          this.errors.amount = this.$store.state.language === 'en'
            ? 'Please enter amount'
            : 'ငွေပမာဏထည့်ပေးပါ။';
          return false;
        }
        if (this.depositData.amount < 2000) {
          this.errors.amount = this.$store.state.language === 'en'
            ? 'Please enter at least 2,000 MMK'
            : 'ငွေပမာဏအနည်းဆုံး 2,000 ထည့်ပေးပါ။';
          return false;
        }
        if (!this.imageUploader.length) {
          this.errors.image = this.$t('deposit.enter_screenshot_photo');
          return false;
        }
        this.isConfirm = true;
        if (this.imageUploader[0]) {
          this.depositData.transaction_image = await this.compressAndConvertToWebP(
            this.imageUploader[0].content,
          );
        }
        this.depositData.account_id = this.account[0].id;
        this.depositData.date = this.currentTime;
        this.depositData.lang = localStorage.getItem('language');
        await axios.post('/deposits', this.depositData, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  },
              });
        this.imageUploader = [];
        this.depositData = {};
        this.selected_account.account_id = null;
        this.accountFilter = '';
        Toast.success(
          this.$store.state.language === 'en'
            ? 'Success'
            : 'အောင်မြင်သည်။',
        );
        this.isConfirm = false;
      } catch (error) {
        Toast.fail(
          this.$store.state.language === 'en' ? 'Fail' : 'ကျရှုံးသည်။',
        );
        this.error = error.response.data;
        this.isConfirm = false;
      }
      return true;
    },
    createDateWithTimeZone() {
      const moment = require('moment-timezone');
      const dateInTimeZone = moment.tz(new Date(), 'Asia/Rangoon');
      const year = dateInTimeZone.year();
      const month = dateInTimeZone.month();
      const day = dateInTimeZone.date();
      const hour = dateInTimeZone.hour();
      const minute = dateInTimeZone.minute();
      const second = dateInTimeZone.second();
      const millisecond = dateInTimeZone.millisecond();
      return new Date(year, month, day, hour, minute, second, millisecond);
    },
    compressAndConvertToWebP(dataUrl, quality = 0.7, maxWidth = 1280) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/webp', quality));
        };
        img.onerror = (err) => reject(err);
        img.src = dataUrl;
      });
    },
  },
  watch: {
    'selected_account.account_id': function () {
      const id = this.selected_account.account_id;
      this.account = this.accounts.filter(
        (account) => account.id == id,
      );
    },
  },
  computed: {
    currentTime() {
      return moment(this.createDateWithTimeZone()).format('lll');
    },
    filteredAccounts() {
      const q = String(this.accountFilter || '').trim().toLowerCase();
      if (!q) return this.accounts;
      return this.accounts.filter((a) => {
        const name = `${a.account_name || ''} ${a.name || ''} ${a.payment_type?.name || ''}`.toLowerCase();
        return name.includes(q);
      });
    },
  },
  async mounted() {
    await this.fetchAccounts();
    const authUser = await this.fetchUser();
    if (!authUser) {
      this.$router.push('/auth/login');
    }
  },
};
</script>
<style scoped>
.deposit-page {
  --finance-accent: rgb(240, 97, 38);
  --finance-accent-soft: rgba(240, 97, 38, 0.22);
  --finance-surface: #141414;
  --finance-surface-2: #1a1a1a;
  --finance-text: #e8e8e8;
  --finance-muted: #9a9a9a;
  --finance-radius: 14px;
}

.finance-page {
  box-sizing: border-box;
  min-height: 100vh;
  overflow-y: visible;
  padding-top: calc(1.35rem + env(safe-area-inset-top, 0px));
  padding-bottom: calc(9.5rem + env(safe-area-inset-bottom, 0px));
  padding-left: 1rem;
  padding-right: 1rem;
  margin-top: 0.35rem;
  margin-bottom: 0.5rem;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, rgba(240, 97, 38, 0.18), transparent),
    linear-gradient(180deg, #0c0c0c 0%, #101010 40%, #0a0a0a 100%);
  -webkit-overflow-scrolling: touch;
}

.finance-page__inner {
  max-width: 520px;
  margin: 0 auto;
}

.finance-hero {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.finance-hero__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--finance-surface);
  border: 1px solid var(--finance-accent-soft);
  color: var(--finance-accent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.finance-hero__title {
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--finance-text);
  margin: 0 0 0.35rem;
  letter-spacing: -0.02em;
}

.finance-hero__subtitle {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--finance-muted);
}

.finance-alert {
  border-radius: var(--finance-radius);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.finance-alert--error {
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.35);
  color: #ffb4b4;
}

.finance-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.finance-card {
  background: var(--finance-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--finance-radius);
  padding: 1.1rem 1.15rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
}

.finance-card--accent {
  border-color: var(--finance-accent-soft);
  background: linear-gradient(145deg, var(--finance-surface-2) 0%, var(--finance-surface) 100%);
}

.finance-card__label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--finance-muted);
  margin: 0 0 0.45rem;
}

.finance-card__label--inline {
  margin: 0;
}

.finance-account-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
}

.finance-account-section__count {
  flex-shrink: 0;
  min-width: 1.5rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--finance-accent);
  background: var(--finance-accent-soft);
  border: 1px solid rgba(240, 97, 38, 0.35);
}

.finance-select-hint {
  margin: 0 0 0.85rem;
  font-size: 0.76rem;
  line-height: 1.45;
  color: var(--finance-muted);
}

.finance-method-empty {
  padding: 1rem 0.75rem;
  text-align: center;
  font-size: 0.82rem;
  color: var(--finance-muted);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.finance-method-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.finance-method-filter {
  width: 100%;
  margin-bottom: 0.15rem;
  padding: 0.55rem 0.85rem 0.55rem 2.25rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #0a0a0a url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'/%3E%3C/svg%3E") no-repeat 0.75rem center;
  color: var(--finance-text);
  font-size: 0.88rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.finance-method-filter::placeholder {
  color: #5c5c5c;
}

.finance-method-filter:focus {
  outline: none;
  border-color: rgba(240, 97, 38, 0.45);
  box-shadow: 0 0 0 3px rgba(240, 97, 38, 0.12);
}

.finance-method-filter-empty {
  margin: 0;
  padding: 0.65rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--finance-muted);
}

.finance-method-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.65rem 0.75rem;
  margin: 0;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: linear-gradient(165deg, rgba(30, 30, 30, 0.95) 0%, rgba(12, 12, 12, 0.98) 100%);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.12s ease;
}

.finance-method-row:hover {
  border-color: rgba(255, 255, 255, 0.12);
}

.finance-method-row:active {
  transform: scale(0.992);
}

.finance-method-row:focus-visible {
  outline: none;
  border-color: rgba(240, 97, 38, 0.55);
  box-shadow: 0 0 0 3px rgba(240, 97, 38, 0.15);
}

.finance-method-row.is-selected {
  border-color: rgba(240, 97, 38, 0.55);
  background: linear-gradient(
    165deg,
    rgba(240, 97, 38, 0.14) 0%,
    rgba(18, 18, 18, 0.98) 55%
  );
  box-shadow:
    0 0 0 1px rgba(240, 97, 38, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.35);
}

.finance-method-row__logo {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.finance-method-row__logo img {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 10px;
}

.finance-method-row__logo-fallback {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--finance-accent);
  text-transform: uppercase;
}

.finance-bank-head {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.finance-bank-head__logo {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.finance-bank-head__logo img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
}

.finance-method-row__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.finance-method-row__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #f4f4f4;
  line-height: 1.25;
}

.finance-method-row__sub {
  font-size: 0.72rem;
  color: #8e8e93;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.finance-method-row__radio {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.finance-method-row.is-selected .finance-method-row__radio {
  border-color: var(--finance-accent);
  background: rgba(240, 97, 38, 0.2);
}

.finance-method-row__radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.18s ease, transform 0.18s ease;
}

.finance-method-row.is-selected .finance-method-row__radio-dot {
  background: var(--finance-accent);
  transform: scale(1);
}

.finance-method-row:not(.is-selected) .finance-method-row__radio-dot {
  transform: scale(0);
}

.finance-card--account-select {
  padding: 1.15rem 1.1rem 1.2rem;
  background: linear-gradient(160deg, rgba(24, 24, 24, 0.98) 0%, rgba(14, 14, 14, 1) 100%);
  border-color: rgba(240, 97, 38, 0.12);
}

.finance-bank-head__name {
  font-size: 0.95rem;
  color: var(--finance-text);
  font-weight: 500;
}

.finance-account-box {
  margin-top: 0.75rem;
  text-align: center;
  padding: 1rem;
  border-radius: 12px;
  background: #0a0a0a;
  border: 1px dashed var(--finance-accent-soft);
}

.finance-account-box__label {
  display: block;
  font-size: 0.9rem;
  color: var(--finance-muted);
}

.finance-account-box__number {
  margin: 0.5rem 0 0.85rem;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #fff;
  word-break: break-all;
}

.finance-btn-copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 1.1rem;
  border-radius: 999px;
  border: 1px solid var(--finance-accent);
  background: rgba(240, 97, 38, 0.12);
  color: var(--finance-accent);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
}

.finance-btn-copy:active {
  transform: scale(0.98);
}

.finance-note {
  margin: 0.85rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--finance-muted);
}

.finance-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--finance-text);
  margin-bottom: 0.25rem;
}

.finance-hint {
  margin: 0 0 0.6rem;
  font-size: 0.78rem;
  color: var(--finance-muted);
  line-height: 1.4;
}

.finance-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #0a0a0a;
  color: #fff;
  font-size: 1.1rem;
  text-align: center;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.finance-input:focus {
  outline: none;
  border-color: var(--finance-accent-soft);
  box-shadow: 0 0 0 3px rgba(240, 97, 38, 0.15);
}

.finance-input::placeholder {
  color: #555;
}

.finance-textarea {
  min-height: 5.5rem;
  text-align: left;
  resize: vertical;
  line-height: 1.45;
}

.finance-uploader-wrap {
  display: flex;
  justify-content: center;
  padding: 0.35rem 0 0.15rem;
}

.finance-field-error {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #ff8a8a;
}

.finance-btn-primary {
  margin-top: 0.25rem;
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(180deg, #ff8c42 0%, var(--finance-accent) 55%, #c94a18 100%);
  color: #0d0d0d;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(240, 97, 38, 0.35);
  transition: opacity 0.2s ease, transform 0.1s ease;
}

.finance-btn-primary:disabled {
  opacity: 0.75;
  cursor: wait;
}

.finance-btn-primary:active:not(:disabled) {
  transform: scale(0.99);
}

.finance-btn-primary__spin {
  padding: 0 !important;
}
</style>
